import api from "../utils/api";
import { UserWithSettingsModel, CompanyWithSettingsModel, ContactWithSettings, CompanySettings } from "../account/models/SettingsModels";
import { PersonModelBase, CompanyModel, RegisteredModel, CompanyContactModel, PhoneNumberSettingsModel, EmailSettingsModel, CompanyDataModel } from "../common/models/CommonModels";
import * as moment from 'moment';
import { RegisteredPersonModel, RegisteredCompanyModel } from "../admin/models/CompanyModels";

interface UserWithSettingsModelDto extends PersonModelBase<ContactWithSettings> {
    registeredAt: string
}

/**
 * Transform company dto date times to moment.
 * @param company Company dto to transform.
 */
const convertCompany = (company: CompanyDto): CompanyWithSettingsModel => {
    return Object.assign({}, company, {
        acceptedAt: company.acceptedAt ? moment(company.acceptedAt) : undefined,
        employees: company.employees ? company.employees.map(emp => Object.assign({}, emp, { registeredAt: moment(emp.registeredAt) })) : undefined
    });
}

export const getUserData = (userId: string): Promise<UserWithSettingsModel | undefined> => {
    return api.get(`api/settings/user/${userId}`).then(response => {
        if (response.status === 200) {
            const user = <UserWithSettingsModelDto>response.data;

            return Object.assign({}, user,
                {
                    registeredAt: moment(user.registeredAt),
                });
        }

        return undefined;
    }).catch(error => {
        return undefined;
    });
}

interface CompanyDto extends CompanyDataModel<PhoneNumberSettingsModel, EmailSettingsModel>, RegisteredModel, CompanySettings {
    accepted: boolean;
    acceptedAt?: string;
    employees?: RegisteredPersonModel[];
}

export const getCompanyData = (companyId: string): Promise<CompanyWithSettingsModel | undefined> => {
    return api.get(`api/settings/company/${companyId}`).then(response => {
        if (response.status === 200) {
            const company = <CompanyDto>response.data;

            return convertCompany(company);
        }

        return undefined;
    }).catch(error => {
        return undefined;
    });
}

export const generateApiKey = (companyId: string): Promise<string | undefined> => {
    return api.get(`api/settings/company/apikey/${companyId}`).then(response => {
        if (response.status === 200) {
            const apiKey = <string>response.data;
            return apiKey;
        }
        return undefined;
    }).catch(error => {
        return undefined;
    });
}

export const getTestPartnerApiKey = (): Promise<string | undefined> => {
    return api.get("api/settings/testpartnerapikey").then(response => {
        if (response.status === 200) {
            return response.data as string;
        }

        return undefined;
    }).catch(error => {
        return undefined;
    });
}

export const setPersonalEmailVisibility = (userId: string, visible: boolean): Promise<boolean> => {

    const payload = { userId, visible };

    return api.patch("api/settings/user/emailvisibility", payload).then(response => {
        if (response.status === 200) {
            return true;
        }

        return false;
    });
}

export const setPersonalPhoneVisibility = (userId: string, visible: boolean): Promise<boolean> => {
    const payload = { userId, visible };

    return api.patch("api/settings/user/phonevisibility", payload).then(response => {
        if (response.status === 200) {
            return true;
        }

        return false;
    })
}

export const editUser = (user: UserWithSettingsModel): Promise<UserWithSettingsModel | undefined> => {
    return api.patch("api/settings/user", user).then(response => {
        if (response.status === 200) {
            const result = <UserWithSettingsModel>response.data;
            return result;
        }

        return undefined;
    });
}

export const editCompany = (company: CompanyWithSettingsModel): Promise<CompanyWithSettingsModel | undefined> => {
    const dto = Object.assign({}, company, { employees: undefined })

    return api.patch("api/settings/company", dto).then(response => {
        if (response.status === 200) {
            const result = <CompanyDto>response.data;
            return convertCompany(result);
        }

        return undefined;
    });
}

export const addCompanyPhoneNumber = (dto: { companyId: string, phoneNumber: string, phoneDetails: string }): Promise<CompanyWithSettingsModel | undefined> => {
    return api.post("api/settings/company/phonenumber", dto).then(response => {
        if (response.status === 200) {
            const result = <CompanyDto>response.data;
            return convertCompany(result);
        }

        return undefined;
    });
}

export const addCompanyEmail = (dto: { companyId: string, email: string, emailDetails: string }): Promise<CompanyWithSettingsModel | undefined> => {
    return api.post("api/settings/company/email", dto).then(response => {
        if (response.status === 200) {
            const result = <CompanyDto>response.data;

            return convertCompany(result);
        }

        return undefined;
    });
}

export const deletePhoneNumber = (dto: { contactId: string, companyId: string }): Promise<CompanyWithSettingsModel | undefined> => {
    return api.delete("api/settings/company/phonenumber", dto).then(response => {
        if (response.status === 200) {
            const result = <CompanyDto>response.data;

            return convertCompany(result);
        }

        return undefined;
    })
}

export const deleteEmail = (dto: { contactId: string, companyId: string }): Promise<CompanyWithSettingsModel | undefined> => {
    return api.delete("api/settings/company/email", dto).then(response => {
        if (response.status === 200) {
            return convertCompany(<CompanyDto>response.data);
        }

        return undefined;
    })
}


// WEBPACK FOOTER //
// ./src/api/settingsApi.ts